import React, { FC, useState } from 'react';
import {
  CrewProgressTreeItemContainerStyled,
  CrewProgressTreeItemPersonaStyled,
  ExpandedItemBorderStyled,
  ExpandCollapseIconStyled,
} from './styled';
import { IconSize, Text, Spinner, SpinnerSize } from '@bechtel/gds';
import { CrewProgressTree } from '~/superintendent/crew-progress/crew-progress-tree';
import { WelderProgressTree } from '~/superintendent/crew-progress/welder-progress-tree';
import { UserInfo } from '~/components/organisms/user-info';
import { find, get } from 'lodash';
import {
  CrewProgressStatsFilterByTerm,
  FilterOperator,
  PersonaType,
  CommodityFilterStatusFlag,
  CrewProgressStats,
} from '@bechtel/types';
import { ErrorRetry, useAppUser } from '@bechtel/client-common';
import { Trans } from '@lingui/macro';
import { useCrewProgressStatsQuery } from '~/hooks/use-crew-progress-stats';

export interface CrewProgressTreeItemProps {
  weldStatsByPersona: CrewProgressStats;
  depth: number;
}
export const CrewProgressTreeItem: FC<CrewProgressTreeItemProps> = ({ weldStatsByPersona, depth }) => {
  const [expanded, setExpanded] = useState(false);
  const { crewMember, commodityStatusStats } = weldStatsByPersona;
  const { user } = useAppUser();
  const currentProjectWeek = find(user?.project?.projectConfiguration?.projectWeeks, (projectWeek) =>
    projectWeek?.isCurrent ? true : false
  );

  const getStats = (status: CommodityFilterStatusFlag) => {
    const stats = commodityStatusStats
      ?.filter((item) => item?.status === status)
      .reduce((acc, item) => (acc += item?.stats || 0), 0);
    return stats;
  };

  const [loadCrewStats, { loading, data, error, refetch }] = useCrewProgressStatsQuery(true);

  const handleClick = (crewMemberId: string, persona: PersonaType) => {
    if (!expanded) {
      setExpanded(true);
      loadCrewStats({
        variables: {
          input: {
            complexFilter: {
              and: [
                {
                  term: CrewProgressStatsFilterByTerm.CREW_MEMBER_ID,
                  operator: FilterOperator.EQUALS,
                  value: crewMemberId,
                },
                {
                  term: CrewProgressStatsFilterByTerm.PERSONA_TYPE,
                  operator: FilterOperator.EQUALS,
                  value: persona,
                },
                {
                  term: CrewProgressStatsFilterByTerm.INCLUDE_BLOCKED_COMMODITY,
                  operator: FilterOperator.EQUALS,
                  value: 'true',
                },
                {
                  term: CrewProgressStatsFilterByTerm.START_DATE,
                  operator: FilterOperator.GREATER_THAN,
                  value: currentProjectWeek?.startDate,
                },
                {
                  term: CrewProgressStatsFilterByTerm.END_DATE,
                  operator: FilterOperator.LESS_THAN,
                  value: currentProjectWeek?.endDate,
                },
              ],
            },
          },
          paging: {
            limit: 20,
            cursor: '1',
          },
        },
      });
    } else {
      setExpanded(false);
    }
  };

  const crewStatsData: CrewProgressStats[] = get(data, 'crewProgressStats.items', []);
  return (
    <>
      <CrewProgressTreeItemContainerStyled depth={depth}>
        <CrewProgressTreeItemPersonaStyled>
          <ExpandCollapseIconStyled
            size={IconSize.MEDIUM}
            icon={!expanded ? 'plus-circle' : 'minus-circle'}
            onClick={() => handleClick(weldStatsByPersona?.crewMemberId, crewMember?.persona)}
          ></ExpandCollapseIconStyled>
          {expanded && <ExpandedItemBorderStyled />}
          <UserInfo crewMember={crewMember} />
        </CrewProgressTreeItemPersonaStyled>
        <Text size="400" color="grey.600" family="graphik" weight={expanded && depth > 1 ? '600' : '400'}>
          {getStats(CommodityFilterStatusFlag.READY_TO_WELD)}
        </Text>
        <Text size="400" color="grey.600" family="graphik" weight={expanded && depth > 1 ? '600' : '400'}>
          {getStats(CommodityFilterStatusFlag.CLAIMED)}
        </Text>
        <Text size="400" color="grey.600" family="graphik" weight={expanded && depth > 1 ? '600' : '400'}>
          {getStats(CommodityFilterStatusFlag.VT_SENT_BACK)}
        </Text>
      </CrewProgressTreeItemContainerStyled>

      {loading && !error && expanded && <Spinner size={SpinnerSize.SMALL} />}
      {!loading && !error && (
        <>
          {expanded &&
            (crewMember?.persona == PersonaType.GENERAL_FOREMAN ||
              crewMember?.persona == PersonaType.SUPERINTENDENT) && (
              <CrewProgressTree depth={depth + 1} data={crewStatsData}></CrewProgressTree>
            )}
          {expanded && crewMember?.persona == PersonaType.FOREMAN && (
            <WelderProgressTree craftStats={crewStatsData} depth={depth + 1}></WelderProgressTree>
          )}
        </>
      )}
      {!loading && error && (
        <div data-testid="foreman-craft-error">
          <ErrorRetry refetch={refetch}>
            <Trans>There was a problem loading crew data.</Trans>
          </ErrorRetry>
        </div>
      )}
    </>
  );
};
